import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) {
            toast.success("Welcome back, curator.");
            const from = location.state?.from || "/admin";
            navigate(from);
        } else {
            setError(res.error);
        }
    };

    return (
        <div data-testid="login-page" className="mx-auto grid min-h-[calc(100vh-80px)] max-w-6xl grid-cols-1 md:grid-cols-2 gap-16 px-6 py-16 lg:px-10">
            <div className="hidden md:flex flex-col justify-between border border-border p-10 scanlines bg-card">
                <div>
                    <div className="overline">// Auth.sys</div>
                    <h1 className="mt-6 font-serif text-6xl leading-[0.95]">
                        Enter the<br />
                        <span className="italic text-phosphor-amber glow-amber">curator&apos;s</span><br />terminal.
                    </h1>
                </div>
                <div className="font-mono text-xs text-muted-foreground space-y-1">
                    <div>&gt; SYS_VERSION ····· 1.0.0</div>
                    <div>&gt; CIPHER ········· BCRYPT-JWT</div>
                    <div>&gt; STATUS ········· <span className="text-phosphor-green">READY</span></div>
                </div>
            </div>

            <div className="flex flex-col justify-center">
                <div className="overline">// Log in</div>
                <h2 className="mt-4 font-serif text-5xl">Authenticate</h2>
                <p className="mt-3 font-sans text-sm text-muted-foreground">
                    Curator credentials required to modify the archive.
                </p>

                <form onSubmit={submit} className="mt-10 space-y-6" data-testid="login-form">
                    <label className="block">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Email</div>
                        <input
                            data-testid="login-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full border border-border bg-card px-4 py-3 font-mono text-sm outline-none focus:border-phosphor-amber transition-colors"
                            placeholder="curator@vintage.io"
                        />
                    </label>
                    <label className="block">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Password</div>
                        <input
                            data-testid="login-password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full border border-border bg-card px-4 py-3 font-mono text-sm outline-none focus:border-phosphor-amber transition-colors"
                            placeholder="••••••••"
                        />
                    </label>

                    {error && (
                        <div data-testid="login-error" className="border border-destructive/60 bg-destructive/10 px-4 py-3 font-mono text-xs text-destructive">
                            &gt; {error}
                        </div>
                    )}

                    <button
                        data-testid="login-submit"
                        type="submit"
                        disabled={loading}
                        className="w-full border border-phosphor-amber bg-phosphor-amber px-6 py-3 font-mono text-xs uppercase tracking-[0.3em] text-background hover:bg-transparent hover:text-phosphor-amber transition-colors disabled:opacity-50"
                    >
                        {loading ? "Authenticating…" : "Log in ▸"}
                    </button>
                </form>

                <div className="mt-8 font-mono text-xs text-muted-foreground">
                    New to the archive?{" "}
                    <Link to="/register" data-testid="link-to-register" className="text-phosphor-amber hover:underline">
                        Request curator access
                    </Link>
                </div>
            </div>
        </div>
    );
}
